import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CheckCircle2, Copy, Loader2, Mail, X } from 'lucide-react';
import { TextInput, SelectInput, TextArea } from '../../ui/form';
import { POSITION_OPTIONS } from '../../application/stepConfig';
import driverApplicationService, { applyServerFieldErrors, errorMessage } from '../../../services/driverApplicationService';

const EXPIRY_OPTIONS = [3, 7, 14, 30].map((d) => ({ value: String(d), label: `${d} days` }));

/**
 * "Invite driver" dialog: collects name + email, sends the invite email, and
 * shows the link so it can also be texted to the applicant.
 */
const InviteDriverModal = ({ open, onClose, onInvited }) => {
  const { register, handleSubmit, setError, reset, formState: { errors } } = useForm({
    defaultValues: { firstName: '', lastName: '', email: '', positionType: 'company_driver', message: '', expiresInDays: '7' }
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [formError, setFormError] = useState('');
  const [copied, setCopied] = useState(false);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (open) {
      reset();
      setResult(null);
      setFormError('');
      setCopied(false);
      setTimeout(() => firstFieldRef.current?.focus(), 50);
    }
  }, [open, reset]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const submit = handleSubmit(async (values) => {
    setBusy(true);
    setFormError('');
    try {
      const data = await driverApplicationService.createInvite({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        positionType: values.positionType,
        message: values.message?.trim() || null,
        expiresInDays: Number(values.expiresInDays)
      });
      setResult(data);
      onInvited?.(data.application);
    } catch (error) {
      const applied = applyServerFieldErrors(error, setError);
      if (!applied) setFormError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (error) {
      setCopied(false);
    }
  };

  const { ref: firstNameRef, ...firstNameField } = register('firstName', { required: 'First name is required' });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-labelledby="invite-title">
      <div className="absolute inset-0 bg-gray-900/60" onClick={() => !busy && onClose()} aria-hidden="true" />
      <div className="relative flex max-h-[92vh] w-full flex-col rounded-t-2xl bg-white shadow-xl sm:max-w-lg sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 id="invite-title" className="text-lg font-semibold text-gray-900">{result ? 'Invite sent' : 'Invite a driver to apply'}</h2>
          <button type="button" onClick={onClose} disabled={busy} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {result ? (
          <div className="space-y-4 px-5 py-5">
            <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" aria-hidden="true" />
              <div className="text-sm text-green-900">
                <p className="font-semibold">{result.application.applicant.firstName} {result.application.applicant.lastName} has been invited.</p>
                <p className="mt-1">
                  {result.emailSent
                    ? `An email with their personal application link was sent to ${result.application.applicant.email}.`
                    : `Email sending is not configured on the server, so share the link below with ${result.application.applicant.email} yourself.`}
                </p>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Application link</p>
              <p className="mt-0.5 text-xs text-gray-500">Only the invited email address can use it. It expires on {new Date(result.application.invite.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.</p>
              <div className="mt-2 flex gap-2">
                <input readOnly value={result.inviteUrl} className="block w-full truncate rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-xs text-gray-700" onFocus={(e) => e.target.select()} />
                <button type="button" onClick={copyLink} className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                  <Copy className="h-4 w-4" aria-hidden="true" /> {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { setResult(null); reset(); }} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Invite another</button>
              <button type="button" onClick={onClose} className="btn-primary">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
              {formError && <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{formError}</p>}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <TextInput label="First name" required autoComplete="off" ref={(el) => { firstNameRef(el); firstFieldRef.current = el; }} {...firstNameField} error={errors.firstName?.message} />
                <TextInput label="Last name" required autoComplete="off" {...register('lastName', { required: 'Last name is required' })} error={errors.lastName?.message} />
              </div>
              <TextInput label="Email address" required type="email" inputMode="email" autoComplete="off" leftIcon={Mail} hint="The application link is locked to this address." {...register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' } })} error={errors.email?.message} />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <SelectInput label="Position" placeholder={null} options={POSITION_OPTIONS} {...register('positionType')} />
                <SelectInput label="Link expires in" placeholder={null} options={EXPIRY_OPTIONS} {...register('expiresInDays')} />
              </div>
              <TextArea label="Personal message" optionalLabel rows={3} placeholder="e.g. Great talking with you today. Here's the application we discussed." {...register('message', { maxLength: { value: 1000, message: 'Keep it under 1000 characters' } })} error={errors.message?.message} />
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <button type="button" onClick={onClose} disabled={busy} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <Mail className="mr-2 h-4 w-4" aria-hidden="true" />}
                Send invite
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default InviteDriverModal;
